import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import Table from "react-bootstrap/Table";
import Pagination from "react-responsive-pagination";
import { useDispatch } from "react-redux";
import { setLoading } from "../redux/loaderAction";
import { dateFormat, currentDateFormat } from "../utils";
import { BILLING_SUMMARY, GET_ALL_PAYER, GET_SCHEPASSENGER_LIST } from "../constant";
import Swal from "../messageBox/customSweetAlert";
import SearchComponent from "../components/SearchComponent";

const DEFAULT_LIMIT = 10;

const BILL_STATUS_LABELS = {
  1: "Ready",
  2: "Submitted",
  3: "Processing",
  4: "Rejected",
};

function buildBillingSummaryParams({
  pageNum,
  limit,
  billedDateFrom,
  billedDateTo,
  selectedPassenger,
  payer,
}) {
  return {
    page: pageNum,
    limit,
    startDate: billedDateFrom ? moment(billedDateFrom).format("YYYY-MM-DD") : "",
    endDate: billedDateTo ? moment(billedDateTo).format("YYYY-MM-DD") : "",
    passengerId: selectedPassenger?.value?.user_id ?? "",
    payerId: payer && payer !== "0" ? payer : "",
  };
}

export default function BillingSummaryPage() {
  const dispatch = useDispatch();
  const [list, setList] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: DEFAULT_LIMIT,
  });
  const [currentDate] = useState(new Date());
  const [billedDateFrom, setBilledDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  });
  const [billedDateTo, setBilledDateTo] = useState(() => new Date());
  const [errors, setErrors] = useState({});
  const [limit] = useState(DEFAULT_LIMIT);
  const [payer, setPayer] = useState("0");
  const [payerList, setPayerList] = useState([]);
  const [passengerList, setPassengerList] = useState([]);
  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const appliedFiltersRef = useRef(null);
  const hasInitialFetchedRef = useRef(false);

  const passengerListWithFullName = useMemo(
    () =>
      passengerList.map((passenger) => {
        const fullName = `${passenger.first_name || ""} ${
          passenger.middle_name || ""
        } ${passenger.last_name || ""} ${passenger.passenger?.pass_pmi_no || ""}`
          .replace(/\s+/g, " ")
          .trim()
          .toLowerCase();

        return {
          ...passenger,
          full_name: fullName,
        };
      }),
    [passengerList]
  );

  useEffect(() => {
    const loadFilterData = async () => {
      try {
        const [payerRes, passengerRes] = await Promise.all([
          axios.get(GET_ALL_PAYER),
          axios.get(GET_SCHEPASSENGER_LIST),
        ]);
        setPayerList(payerRes.data);
        setPassengerList(passengerRes.data);
      } catch (error) {
        console.error("Error loading billing summary filters:", error);
      }
    };
    loadFilterData();
  }, []);

  const fetchBillingSummary = useCallback(
    async (pageNum = 1, filters) => {
      if (!filters) return;

      dispatch(setLoading(true));
      try {
        const params = buildBillingSummaryParams({
          pageNum,
          limit,
          billedDateFrom: filters.billedDateFrom,
          billedDateTo: filters.billedDateTo,
          selectedPassenger: filters.selectedPassenger,
          payer: filters.payer,
        });
        const response = await axios.get(BILLING_SUMMARY, { params });
        const isOk =
          response?.data?.success === true ||
          response?.data?.status === 200 ||
          response?.data?.status === "200";
        if (isOk) {
          setList(response.data.data || []);
          const p = response.data.pagination || {};
          setPagination({
            currentPage: p.currentPage ?? pageNum,
            totalPages: Math.max(1, p.totalPages ?? 1),
            totalRecords: p.totalRecords ?? 0,
            recordsPerPage: p.recordsPerPage ?? limit,
          });
        } else {
          setList([]);
          setPagination((prev) => ({ ...prev, totalPages: 1, totalRecords: 0 }));
        }
      } catch (err) {
        console.error(err);
        Swal.fire("Error", err.response?.data?.message || "Failed to load billing summary", "error");
        setList([]);
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch, limit]
  );

  useEffect(() => {
    if (hasInitialFetchedRef.current) return;
    hasInitialFetchedRef.current = true;

    const defaultFilters = {
      billedDateFrom,
      billedDateTo,
      selectedPassenger: null,
      payer: "0",
    };
    appliedFiltersRef.current = defaultFilters;
    fetchBillingSummary(1, defaultFilters);
  }, [fetchBillingSummary]);

  const handleSearch = () => {
    if (billedDateFrom && billedDateTo && billedDateFrom > billedDateTo) {
      setErrors({ billed_date: "From date cannot be after To date" });
      return;
    }
    setErrors({});
    const filters = {
      billedDateFrom,
      billedDateTo,
      selectedPassenger,
      payer,
    };
    appliedFiltersRef.current = filters;
    fetchBillingSummary(1, filters);
  };

  const handlePayerChange = (e) => {
    setPayer(e.target.value);
  };

  const handlePageChange = (newPage) => {
    if (appliedFiltersRef.current) {
      fetchBillingSummary(newPage, appliedFiltersRef.current);
    }
  };

  const getPassengerName = (row) => {
    const passenger = row.passenger;
    if (!passenger) return "—";
    const name = `${passenger.first_name || ""} ${passenger.middle_name || ""} ${
      passenger.last_name || ""
    }`
      .replace(/\s+/g, " ")
      .trim();
    return name || "—";
  };

  const getBilledAmount = (row) => {
    const amount =
      row.total_amount?.total_amount ??
      (typeof row.total_amount === "number" ? row.total_amount : null) ??
      row.bill_amount;
    if (amount === "" || amount == null) return "—";
    return `$${Number(amount).toFixed(2)}`;
  };

  return (
    <>
      <section style={{ borderBottom: "solid 1px #eee", marginTop: "0px" }}>
        <div className="d-flex justify-content-between form-container">
          <div>
            <h2 className="fs-5 font-weight-600">Billing Summary</h2>
          </div>
          <div>
            <span>{currentDateFormat(currentDate)}</span>
          </div>
        </div>
      </section>

      <section className="filter-section">
        <div
          className="d-flex flex-wrap align-items-end gap-2 p-3 mb-3"
          style={{ background: "rgb(205 228 218 / 29%)" }}
        >
          <div style={{ minWidth: "160px" }}>
            <SearchComponent
              data={passengerListWithFullName}
              searchFields={["full_name"]}
              placeholder="Search Passenger"
              label="Passenger"
              onSelect={(passenger) => {
                setSelectedPassenger(passenger);
              }}
            />
          </div>
          <div>
            <label className="d-block">Payor</label>
            <select
              className="form-select"
              name="payer"
              value={payer}
              onChange={handlePayerChange}
              style={{ minWidth: "160px" }}
            >
              <option value="0">All</option>
              {payerList.map((payerItem, index) => (
                <option key={index} value={payerItem.id}>
                  {payerItem.payer_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="d-block">From</label>
            <DatePicker
              selected={billedDateFrom}
              onChange={(date) => {
                setBilledDateFrom(date);
                if (errors.billed_date) setErrors({});
              }}
              dateFormat="MM/dd/yyyy"
              placeholderText="Select date"
              className="form-control"
              style={{ minWidth: "140px" }}
            />
          </div>
          <div>
            <label className="d-block">To</label>
            <DatePicker
              selected={billedDateTo}
              onChange={(date) => {
                setBilledDateTo(date);
                if (errors.billed_date) setErrors({});
              }}
              dateFormat="MM/dd/yyyy"
              placeholderText="Select date"
              className="form-control"
              style={{ minWidth: "140px" }}
            />
          </div>
          {errors.billed_date && (
            <div className="text-danger small align-self-end">{errors.billed_date}</div>
          )}
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-primary rounded-1"
              style={{ background: "#31876f", border: "unset", color: "#fff" }}
              onClick={handleSearch}
            >
              Search
            </button>
          </div>
        </div>
      </section>

      <section>
        <div className="table-responsive">
          <Table striped bordered hover className="tbl-require">
            <thead>
              <tr>
                <th>Billed Date</th>
                <th>Trip Date</th>
                <th>Passenger</th>
                <th>Billed Amount</th>
                <th>Billing Status</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", color: "gray" }}>
                    No data available
                  </td>
                </tr>
              ) : (
                list.map((row, index) => (
                  <tr key={row.schedule_id || index}>
                    <td>{row.bill_date ? dateFormat(row.bill_date) : "—"}</td>
                    <td>{row.schedule_date ? dateFormat(row.schedule_date) : "—"}</td>
                    <td>{getPassengerName(row)}</td>
                    <td>{getBilledAmount(row)}</td>
                    <td>
                      {row.bill_status != null
                        ? BILL_STATUS_LABELS[row.bill_status] || row.bill_status
                        : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
        {pagination.totalPages > 1 && (
          <div className="d-flex justify-content-center mt-2">
            <Pagination
              current={pagination.currentPage}
              total={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
        {pagination.totalRecords > 0 && (
          <div className="small text-muted text-center mt-1">
            Total records: {pagination.totalRecords}
          </div>
        )}
      </section>
    </>
  );
}
